import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, Lock, MapPin, Target, Sparkles, Backpack, Compass, Award, Plus, ClipboardList, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

const card = "bg-white/[0.04] border border-white/10 rounded-xl p-6 flex flex-col";
const overline = "hr-overline text-blaze text-[11px]";

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-serif text-3xl text-bone leading-none">{value}</div>
      <div className="text-bone/50 text-xs uppercase tracking-wider mt-1.5">{label}</div>
    </div>
  );
}

export default function MyHomeRange() {
  const { isAuthed, ready, user } = useAuth();
  const { openAuth, openAI, setBuildHuntOpen } = useUI();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!isAuthed) return;
    Promise.all([
      api.huntSummary().catch(() => ({ hunt: null })),
      api.myPoints().catch(() => []),
      api.passport().catch(() => null),
      api.followedStates().catch(() => ({ states: [], suggested: [] })),
      api.homeRangeProfile().catch(() => ({ profile: {} })),
      api.applications().catch(() => ({ applications: [] })),
      api.myHuntInvites().catch(() => ({ invites: [] })),
    ]).then(([summary, points, passport, followed, prof, apps, inv]) => {
      setData({ summary, points: points || [], passport, followed, profile: prof.profile || {}, apps: apps.applications || [], invites: inv.invites || [] });
    });
  }, [isAuthed]);

  const follow = async (state) => {
    try {
      const r = await api.toggleFollowState(state);
      setData((d) => ({ ...d, followed: { states: r.states, suggested: (d.followed.suggested || []).filter((s) => !r.states.includes(s)) } }));
      toast.success(r.following ? `Following ${state}` : `Unfollowed ${state}`);
    } catch { toast.error("Couldn't update."); }
  };

  if (!ready || (isAuthed && !data)) {
    return <div className="bg-charcoal min-h-screen"><SiteHeader /><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-blaze" /></div></div>;
  }

  if (!isAuthed) {
    return (
      <div className="bg-charcoal min-h-screen" data-testid="my-homerange-gate">
        <SiteHeader />
        <div className="max-w-lg mx-auto text-center py-24 px-5">
          <span className="w-14 h-14 rounded-full bg-white/10 grid place-items-center mx-auto"><Lock className="text-blaze" /></span>
          <h1 className="font-serif text-4xl text-bone mt-5">My HomeRange</h1>
          <p className="text-bone/60 mt-3">Sign in to open your personalized hunting headquarters.</p>
          <button onClick={() => openAuth("login")} className="mt-7 bg-blaze text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-blaze-light transition-colors rounded-full" data-testid="my-homerange-signin">Sign in / Create account</button>
        </div>
      </div>
    );
  }

  const { summary, points, passport, followed, profile, apps } = data;
  const firstName = profile.first_name || (user?.display_name || "").split(" ")[0] || "Hunter";
  const invites = data.invites || [];
  const respondInvite = async (memberId, huntId, accept) => {
    await api.respondInvite(memberId, accept);
    setData((d) => ({ ...d, invites: d.invites.filter((i) => i.member_id !== memberId) }));
    if (accept) { toast.success("You're in — opening the hunt."); navigate(`/hunts/${huntId}`); }
    else toast("Invitation declined.");
  };
  const upcomingApps = (apps || [])
    .filter((a) => a.days_to_deadline !== null && a.days_to_deadline >= 0 && !["successful", "unsuccessful", "skipped"].includes(a.status))
    .sort((a, b) => a.days_to_deadline - b.days_to_deadline).slice(0, 4);
  const hunt = summary?.hunt;
  const topPoints = [...points].sort((a, b) => b.points - a.points).slice(0, 4);
  const aiSeed = "Based on my HomeRange profile — my points, budget, preferred states and hunting style — what are a few realistic hunts I should consider next season?";

  return (
    <div className="bg-charcoal min-h-screen grain" data-testid="my-homerange-page">
      <SiteHeader />
      <main className="max-w-[1400px] mx-auto px-5 sm:px-10 py-10 text-bone">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <span className={overline}>Your HomeRange</span>
            <h1 className="font-serif text-4xl sm:text-5xl mt-2" data-testid="my-homerange-greeting">{greeting()}, {firstName}.</h1>
            <p className="text-bone/50 mt-2">Your season. Your hunts. Your range.</p>
          </div>
          <Link to="/my-homerange/data" className="inline-flex items-center gap-2 text-sm text-bone/60 hover:text-blaze transition-colors border border-white/15 rounded-full px-4 py-2.5" data-testid="link-homerange-data">
            <Lock size={14} /> My HomeRange Data
          </Link>
        </div>

        <div className="grid lg:grid-cols-3 gap-5 mt-8">
          {invites.length > 0 && (
            <div className="lg:col-span-3 bg-blaze/10 border border-blaze/30 rounded-xl p-5" data-testid="hunt-invites">
              <div className="flex items-center gap-2"><Users size={15} className="text-blaze" /><span className={overline}>Hunt invitations</span></div>
              <div className="mt-3 space-y-2.5">
                {invites.map((iv) => (
                  <div key={iv.member_id} className="flex items-center justify-between flex-wrap gap-3" data-testid={`invite-${iv.member_id}`}>
                    <p className="text-bone/85 text-sm"><span className="font-semibold">{iv.invited_by}</span> invited you to <span className="font-semibold">{iv.hunt_title}</span> {iv.state && <span className="text-bone/50">· {iv.state}</span>} <span className="text-bone/40">({iv.role})</span></p>
                    <div className="flex gap-2">
                      <button onClick={() => respondInvite(iv.member_id, iv.hunt_id, true)} className="bg-forest text-bone px-4 py-2 rounded-full text-xs font-semibold hover:bg-forest/80 transition-colors" data-testid={`invite-accept-${iv.member_id}`}>Accept</button>
                      <button onClick={() => respondInvite(iv.member_id, iv.hunt_id, false)} className="border border-white/15 text-bone/60 px-4 py-2 rounded-full text-xs font-semibold hover:text-bone transition-colors" data-testid={`invite-decline-${iv.member_id}`}>Decline</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Next Hunt */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className={`${card} lg:col-span-2`} data-testid="card-next-hunt">
            <div className="flex items-center gap-2"><Compass size={15} className="text-blaze" /><span className={overline}>Next Hunt</span></div>
            {hunt ? (
              <div className="mt-4 flex-1 flex flex-col">
                <h2 className="font-serif text-3xl text-bone">{hunt.title}</h2>
                <p className="text-bone/60 mt-1.5">{hunt.meta}</p>
                <div className="flex flex-wrap gap-2 mt-4">
                  {[hunt.species, hunt.weapon, hunt.unit && `Unit ${hunt.unit}`, hunt.draw_status].filter(Boolean).map((t) => (
                    <span key={t} className="text-xs font-semibold bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-bone/70">{t}</span>
                  ))}
                </div>
                <div className="mt-5 flex items-center gap-6">
                  <div className="flex-1">
                    <div className="flex justify-between text-xs text-bone/50 mb-1.5"><span>Gear readiness</span><span className="text-bone">{hunt.readiness}%</span></div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-forest rounded-full" style={{ width: `${hunt.readiness}%` }} /></div>
                  </div>
                </div>
                <div className="mt-5 flex gap-3">
                  <Link to={`/hunts/${hunt.id}`} className="inline-flex items-center gap-1.5 bg-blaze text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="next-hunt-open">Open Hunt <ArrowRight size={15} /></Link>
                  <Link to="/planner" className="inline-flex items-center gap-1.5 border border-white/15 px-5 py-2.5 rounded-full text-sm font-semibold text-bone/80 hover:text-bone transition-colors">Continue planning</Link>
                </div>
              </div>
            ) : (
              <div className="mt-4 flex-1 flex flex-col justify-center py-6">
                <p className="text-bone/60">You don&apos;t have an upcoming hunt yet.</p>
                <button onClick={() => setBuildHuntOpen(true)} className="mt-4 self-start inline-flex items-center gap-1.5 bg-blaze text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-blaze-light transition-colors" data-testid="next-hunt-build">Build My Hunt <ArrowRight size={15} /></button>
              </div>
            )}
          </motion.div>

          {/* AI Recommendation */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className={`${card} bg-gradient-to-b from-forest/25 to-white/[0.03] border-forest/30`} data-testid="card-ai-rec">
            <div className="flex items-center gap-2"><Sparkles size={15} className="text-blaze" /><span className={overline}>HomeRange AI</span></div>
            <p className="mt-4 text-bone/80 flex-1">Ask HomeRange AI to plan around <span className="text-bone font-semibold">your</span> points, budget and favorite states — it already knows your profile.</p>
            <button onClick={() => openAI(aiSeed, false)} className="mt-5 self-start inline-flex items-center gap-1.5 bg-white/10 hover:bg-blaze hover:text-white text-bone px-5 py-2.5 rounded-full text-sm font-semibold transition-colors" data-testid="ai-rec-open">
              Get recommendations <ArrowRight size={15} />
            </button>
          </motion.div>

          {/* Point Wallet */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className={card} data-testid="card-point-wallet">
            <div className="flex items-center gap-2"><Target size={15} className="text-blaze" /><span className={overline}>Point Wallet</span></div>
            {topPoints.length ? (
              <div className="mt-4 space-y-2.5 flex-1">
                {topPoints.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm">
                    <span className="text-bone/70">{p.state} {p.species}</span>
                    <span className="font-serif text-lg text-bone">{p.points}</span>
                  </div>
                ))}
              </div>
            ) : <p className="mt-4 text-bone/50 text-sm flex-1">Track your preference points across states.</p>}
            <Link to="/applications#points" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blaze hover:gap-2.5 transition-all" data-testid="wallet-open">Open Point Wallet <ArrowRight size={14} /></Link>
          </motion.div>

          {/* Applications & Deadlines */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className={card} data-testid="card-applications">
            <div className="flex items-center gap-2"><ClipboardList size={15} className="text-blaze" /><span className={overline}>Applications & Deadlines</span></div>
            {upcomingApps.length ? (
              <div className="mt-4 space-y-2.5 flex-1">
                {upcomingApps.map((a) => (
                  <div key={a.id} className="flex items-center justify-between text-sm">
                    <span className="text-bone/70 truncate">{a.state} {a.species}</span>
                    <span className={`font-semibold whitespace-nowrap ${a.days_to_deadline <= 14 ? "text-red-300" : "text-bone/60"}`}>in {a.days_to_deadline}d</span>
                  </div>
                ))}
              </div>
            ) : <p className="mt-4 text-bone/50 text-sm flex-1">No upcoming deadlines. Track your draws so nothing slips.</p>}
            <Link to="/applications" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blaze hover:gap-2.5 transition-all" data-testid="applications-open">View Applications <ArrowRight size={14} /></Link>
            <Link to="/deadlines" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-blaze hover:gap-2.5 transition-all" data-testid="deadlines-open">Deadline Calendar <ArrowRight size={14} /></Link>
          </motion.div>

          {/* Gear Readiness */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className={card} data-testid="card-gear-readiness">
            <div className="flex items-center gap-2"><Backpack size={15} className="text-blaze" /><span className={overline}>Gear Readiness</span></div>
            <div className="mt-4 flex-1 flex flex-col justify-center">
              {hunt ? (
                <>
                  <div className="font-serif text-4xl text-bone">{hunt.readiness}%</div>
                  <p className="text-bone/50 text-sm mt-1">{hunt.gear_remaining} item{hunt.gear_remaining === 1 ? "" : "s"} still needed for {hunt.title}.</p>
                </>
              ) : <p className="text-bone/50 text-sm">Build a hunt to see gear readiness.</p>}
            </div>
            <Link to="/gear" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blaze hover:gap-2.5 transition-all" data-testid="gear-open">Open Gear Locker <ArrowRight size={14} /></Link>
          </motion.div>

          {/* Hunt Passport */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className={card} data-testid="card-passport">
            <div className="flex items-center gap-2"><Award size={15} className="text-blaze" /><span className={overline}>Hunt Passport</span></div>
            <div className="mt-5 grid grid-cols-2 gap-y-5 flex-1">
              <Stat value={passport?.total_hunts ?? 0} label="Hunts" />
              <Stat value={passport?.harvests ?? 0} label="Harvests" />
              <Stat value={passport?.states_hunted ?? 0} label="States" />
              <Stat value={passport?.days_known ? passport.days_afield : "—"} label="Days afield" />
            </div>
            <Link to="/passport" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-blaze hover:gap-2.5 transition-all" data-testid="passport-open">View Hunt Passport <ArrowRight size={14} /></Link>
          </motion.div>

          {/* Followed States */}
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className={`${card} lg:col-span-3`} data-testid="card-followed-states">
            <div className="flex items-center gap-2"><MapPin size={15} className="text-blaze" /><span className={overline}>Followed States</span></div>
            {followed.states.length ? (
              <div className="mt-4 flex flex-wrap gap-2.5">
                {followed.states.map((s) => (
                  <button key={s} onClick={() => navigate(`/states/${encodeURIComponent(s)}`)} className="inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:border-forest text-bone px-4 py-2.5 rounded-full text-sm font-semibold transition-colors" data-testid={`followed-state-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                    <MapPin size={13} className="text-forest" /> {s}
                  </button>
                ))}
              </div>
            ) : (
              <p className="mt-4 text-bone/50 text-sm">Follow the states you hunt to keep their season dates, regs and opportunities close.</p>
            )}
            {followed.suggested?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10">
                <span className="text-bone/40 text-xs uppercase tracking-wider">Suggested from your profile</span>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {followed.suggested.map((s) => (
                    <button key={s} onClick={() => follow(s)} className="inline-flex items-center gap-1.5 border border-dashed border-white/20 hover:border-blaze text-bone/70 hover:text-bone px-3.5 py-2 rounded-full text-sm transition-colors" data-testid={`suggest-follow-${s.toLowerCase().replace(/\s+/g, "-")}`}>
                      <Plus size={12} /> {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
